import React, { useEffect, useState } from 'react';
import TwygsEditorNodes from '../nodes/TwygsEditorNodes';
import getTheme from '../themes/TwygsEditorTheme';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import TwitterPlugin from '../plugins/TwitterPlugin';
import YouTubePlugin from '../plugins/YouTubePlugin';
import AutoLinkPlugin from '../plugins/AutoLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import ForceReadOnlyPlugin from '../plugins/ForceReadOnlyPlugin';

interface LexicalViewerProps {
  content: any;
  className?: string;
}

export function LexicalCommentViewer({ content, className = '' }: LexicalViewerProps) {
  const [isReadingMore, setIsReadingMore] = useState(false);
  const [textLength, setTextLength] = useState(0);
  const CHARACTER_LIMIT = 250;

  const extractTextFromContent = (editorState: any) => {
    try {
      const parsedState = editorState;
      const root = parsedState.root;
      let text = '';

      const traverseNodes = (nodes: any[]) => {
        for (const node of nodes) {
          if (node.type === 'text' && node.text) {
            text += node.text;
          }
          if (node.children && Array.isArray(node.children)) {
            traverseNodes(node.children);
          }
        }
      };

      if (root && root.children) {
        traverseNodes(root.children);
      }

      return text;
    } catch (error) {
      console.error('Failed to parse editorState:', error);
      return '';
    }
  };

  const editorText = extractTextFromContent(content?.editorState);
  useEffect(() => {
    setTextLength(editorText.length);
  }, [editorText]);

  const toggleIsShowingMore = () => {
    setIsReadingMore(!isReadingMore);
  };

  const initialEditorState = content ? JSON.stringify(content?.editorState) : undefined;
  const initialConfig = {
    editorState: initialEditorState,
    namespace: 'CommentViewer',
    nodes: [...TwygsEditorNodes],
    onError: (error: Error) => {
      console.error(error);
    },
    editable: false,
    theme: getTheme(false)
  };

  return (
    <LexicalComposer initialConfig={initialConfig as any}>
      <div className={className}>
        <div className="lexical-content-container">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="outline-none"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: !isReadingMore && textLength > CHARACTER_LIMIT ? 3 : 'none',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxHeight: !isReadingMore && textLength > CHARACTER_LIMIT ? '4.5em' : 'none'
                }}
              />
            }
            placeholder={null}
            ErrorBoundary={LexicalErrorBoundary}
          />

          {/* Add only the plugins needed for viewing */}
          <HistoryPlugin />
          <AutoLinkPlugin />
          <ListPlugin />
          <TwitterPlugin />
          <YouTubePlugin />
          <ForceReadOnlyPlugin />
        </div>

        {textLength > CHARACTER_LIMIT && (
          <div onClick={toggleIsShowingMore} className="cursor-pointer">
            <p className="text-sm font-[900] text-growth-green-500 cursor-pointer hover:opacity-80 mt-[8px]">
              {isReadingMore ? 'Show less' : 'Read more'}
            </p>
          </div>
        )}
      </div>
    </LexicalComposer>
  );
}

export default LexicalCommentViewer;
